import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { pedirPuente } from '#uso';

import { Dato, Seccion } from './piezas';
import { haceDias } from './puente';
import type { PlazasPuente } from './puente';

/**
 * Plazas y lista de espera en el Puente (Vadik, 30-oct-2026): quien entra
 * con Google tiene cuenta al momento mientras queden plazas; el resto espera
 * su turno. Solo el admin sube (o baja) las plazas, aquí. Al subirlas, quien
 * espera entra por orden la próxima vez que toque «Continuar con Google».
 */
export function Plazas({
  datos,
  ahora,
  onCambio,
}: {
  datos: PlazasPuente;
  /** Segundos epoch del resumen, para «hace N días». */
  ahora: number;
  onCambio: () => void;
}) {
  const { t } = useTranslation();
  const [plazas, setPlazas] = useState(datos.plazas);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const libres = Math.max(0, datos.plazas - datos.ocupadas);
  const cambiado = plazas !== datos.plazas;
  const minimo = Math.max(1, datos.ocupadas);

  async function guardar() {
    setGuardando(true);
    setError(false);
    try {
      await pedirPuente('/plazas', { metodo: 'POST', cuerpo: { plazas } });
      onCambio();
    } catch {
      setError(true);
    } finally {
      setGuardando(false);
    }
  }

  async function copiar() {
    try {
      await navigator.clipboard.writeText(
        datos.esperando.map(p => p.correo).join(', '),
      );
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2500);
    } catch {
      setCopiado(false);
    }
  }

  return (
    <Seccion
      titulo={t('Places and waiting list')}
      derecha={
        datos.esperando.length > 0 && (
          <Button
            variant="bare"
            onPress={() => void copiar()}
            style={{
              minHeight: 36,
              padding: '0 10px',
              borderRadius: radio.boton,
              fontSize: 13,
              fontWeight: 800,
              color: color.accent,
            }}
          >
            {copiado ? t('Copied') : t('Copy emails')}
          </Button>
        )
      }
    >
      <Dato
        titulo={t('Accounts in use')}
        valor={`${datos.ocupadas} / ${datos.plazas}`}
        tono={libres === 0 ? 'bad' : libres < 3 ? 'warn' : undefined}
      />
      <View
        aria-hidden
        style={{
          height: 8,
          borderRadius: 4,
          background: color.surface2,
          overflow: 'hidden',
        }}
      >
        <View
          style={{
            height: '100%',
            width: `${Math.min(100, (datos.ocupadas / Math.max(1, datos.plazas)) * 100)}%`,
            background: libres === 0 ? color.bad : color.accent,
          }}
        />
      </View>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          marginTop: 4,
        }}
      >
        <Text style={{ ...texto.cuerpo, color: color.fg2, flex: 1 }}>
          <Trans>Places</Trans>
        </Text>
        <Paso
          etiqueta={t('One place less')}
          onPress={() => setPlazas(p => Math.max(minimo, p - 1))}
          isDisabled={plazas <= minimo}
        >
          −
        </Paso>
        <input
          type="number"
          inputMode="numeric"
          aria-label={t('Places')}
          data-testid="puente-plazas"
          value={plazas}
          min={minimo}
          onChange={e => {
            const n = Number(e.target.value);
            if (Number.isInteger(n)) {
              setPlazas(n);
            }
          }}
          style={{
            width: 64,
            height: TACTIL,
            textAlign: 'center',
            fontSize: 17,
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            borderRadius: radio.boton,
            border: `1px solid ${color.line}`,
            background: color.surface,
            color: color.fg,
          }}
        />
        <Paso
          etiqueta={t('One place more')}
          onPress={() => setPlazas(p => p + 1)}
        >
          +
        </Paso>
      </View>
      {cambiado && (
        <Button
          variant="primary"
          onPress={() => void guardar()}
          isDisabled={guardando || plazas < minimo}
          style={{ minHeight: TACTIL, borderRadius: radio.boton }}
        >
          {t('Save {{count}} places', { count: plazas })}
        </Button>
      )}
      {error && (
        <Text style={{ ...texto.secundario, color: color.bad }}>
          <Trans>Couldn't save. Try again.</Trans>
        </Text>
      )}
      <Text style={{ ...texto.secundario, color: color.fg3 }}>
        {libres > 0
          ? t(
              'New people get an account straight away while there are places left.',
            )
          : t(
              'No places left: whoever arrives goes to the waiting list. Add places so they can come in, in order.',
            )}
      </Text>
      {datos.esperando.length === 0 ? (
        <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
          <Trans>Nobody is waiting.</Trans>
        </Text>
      ) : (
        datos.esperando.map((p, i) => (
          <View
            key={p.turno}
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: 10,
              paddingTop: 8,
              borderTop: `1px solid ${color.line}`,
            }}
          >
            <View style={{ minWidth: 0, flex: 1 }}>
              <Text
                style={{
                  ...texto.cuerpo,
                  fontWeight: 700,
                  overflowWrap: 'anywhere',
                }}
              >
                {p.correo}
              </Text>
              <Text style={{ ...texto.secundario, color: color.fg3 }}>
                {t('Turn {{turn}} · waiting since {{when}}', {
                  turn: p.turno,
                  when: haceDias(t, Math.floor((ahora - p.creado) / 86400)),
                })}
                {p.novedades ? ` · ${t('wants news')}` : ''}
              </Text>
            </View>
            <Text
              style={{
                ...texto.secundario,
                fontWeight: 800,
                color: i < libres ? color.ok : color.fg3,
                alignSelf: 'center',
              }}
            >
              {i < libres ? t('Has a place') : t('Waiting')}
            </Text>
          </View>
        ))
      )}
    </Seccion>
  );
}

function Paso({
  etiqueta,
  onPress,
  isDisabled,
  children,
}: {
  etiqueta: string;
  onPress: () => void;
  isDisabled?: boolean;
  children: string;
}) {
  return (
    <Button
      variant="bare"
      aria-label={etiqueta}
      onPress={onPress}
      isDisabled={isDisabled}
      style={{
        width: TACTIL,
        height: TACTIL,
        borderRadius: radio.boton,
        fontSize: 22,
        fontWeight: 800,
        color: color.accent,
        border: `1px solid ${color.line}`,
      }}
    >
      {children}
    </Button>
  );
}
